import React from 'react';
import { Button, Card, Tooltip as AntdTooltip } from 'antd';
import * as BizCharts from 'bizcharts';
import { DownloadOutlined } from '@ant-design/icons';
import DeltaArrowIcon from '../icons/DeltaArrowIcon';
import {
	AXIS_Y_LINE_COLOR,
	AXIS_X_LINE_COLOR,
	POSITIVE_CHANGE_COLOR,
	NEGATIVE_CHANGE_COLOR,
	CHART_PRIMARY_COLOR,
} from './LifeCycleAnalyticsConstants';

const { Chart, Axis, Geom, Tooltip } = BizCharts as any;

const SpendChart = ({
	chartColor,
	currency,
	spendChartData,
	spendSummary,
	selectedPriorityCard,
	formatSpendAxisLabel,
	buildSpendTooltip,
	handleSpendIntervalClick,
	exportSpendCsv,
}) => {
	const barColor = chartColor;
	return (
		<Card
			className="print:!h-auto print:!min-h-0 print:!break-inside-avoid print:[&_.ant-card-body]:!break-inside-avoid print:[&_.ant-card-body]:!px-2 print:[&_.ant-card-body]:!pb-3 print:[&_.ant-card-body]:!pt-4"
			bodyStyle={{ padding: '57px 36px 40px 16px' }}
			title={
				<div className="flex min-w-0 flex-nowrap items-center gap-3">
					<div className="h5medium text-character-title/85 flex items-center gap-1">
						<span className="h5medium text-character-title/85">
							Spend: {currency.format(spendSummary.totalSpend, 0)}{' '}
						</span>
						{spendSummary.percentChange !== null && spendSummary.percentChange !== 0 ? (
							<div
								style={{
									color:
										spendSummary.percentChange > 0 ? POSITIVE_CHANGE_COLOR : NEGATIVE_CHANGE_COLOR,
								}}
								className="flex items-center gap-1"
							>
								<DeltaArrowIcon
									direction={spendSummary.percentChange > 0 ? 'up' : 'down'}
									color={
										spendSummary.percentChange > 0 ? POSITIVE_CHANGE_COLOR : NEGATIVE_CHANGE_COLOR
									}
								/>
								<span className="bodyregular">{Math.abs(spendSummary.percentChange)}%</span>
							</div>
						) : null}
					</div>
				</div>
			}
			extra={
				<div className="flex items-center gap-2">
					{selectedPriorityCard && (
						<AntdTooltip title={selectedPriorityCard.woPriorityName}>
							<span
								className="inline-block min-w-0 max-w-[250px] truncate rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]"
								style={{
									color: selectedPriorityCard.color,
									backgroundColor: selectedPriorityCard.bgColor,
								}}
							>
								{selectedPriorityCard.woPriorityName}
							</span>
						</AntdTooltip>
					)}
					<AntdTooltip title="Download">
						<Button
							className="print:!hidden"
							icon={<DownloadOutlined />}
							onClick={exportSpendCsv}
						/>
					</AntdTooltip>
				</div>
			}
		>
			<div className="w-full min-w-0 print:!break-inside-avoid">
				<Chart
					height={360}
					data={spendChartData.slice(-6)}
					autoFit={true}
					forceFit={true}
					theme="openwrench"
					scale={{
						spendValue: {
							min: 0,
							nice: true,
						},
					}}
					onIntervalClick={handleSpendIntervalClick}
				>
					<Axis
						name="monthLabel"
						title={{
							text: 'Month',
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
							offset: 40,
						}}
						line={{ style: { stroke: AXIS_X_LINE_COLOR } }}
						tickLine={null}
						label={{
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						}}
					/>
					<Axis
						name="spendValue"
						title={{
							text: `Facility Cost (${currency.id})`,
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
							offset: 80,
						}}
						label={{
							formatter: formatSpendAxisLabel,
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						}}
						line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
						tickLine={null}
						grid={{
							line: {
								style: {
									stroke: AXIS_Y_LINE_COLOR,
									lineDash: [4, 4],
								},
							},
						}}
					/>
					<Tooltip
						showTitle={false}
						enterable={true}
						crosshairs={{ type: 'y' }}
						itemTpl={
							'<li data-index={index} style="list-style:none;padding:0;margin:0;">{value}</li>'
						}
						domStyles={{
							'g2-tooltip': {
								padding: '8px 10px',
								boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
								borderRadius: '6px',
								background: 'rgba(255,255,255,0.96)',
							},
							'g2-tooltip-list': {
								margin: '0',
								padding: '0',
							},
						}}
					/>
					<Geom
						type="interval"
						size={40}
						position="monthLabel*spendValue"
						color={barColor}
						style={{ cursor: 'pointer' }}
						tooltip={['monthFull*spendValue*monthKey', buildSpendTooltip]}
					/>
				</Chart>
			</div>
		</Card>
	);
};

export default SpendChart;
